import { useEffect, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

interface AiCost {
  window_days: number;
  totals: {
    total_calls: number;
    success_count: number;
    cached_count: number;
    failure_count: number;
    tokens_in: number;
    tokens_out: number;
    cost_cents: number;
  };
  by_feature: Array<{ feature: string; calls: number; cost_cents: number; tokens: number }>;
  by_day: Array<{ day: string; cost_cents: number; calls: number }>;
}

export function AiCostPage() {
  const [data, setData] = useState<AiCost | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api<{ data: AiCost }>(`/admin/ai-cost?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [days]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight">AI cost</h1>
          <p className="text-sm text-muted-foreground">
            LLM usage + cost rollup (last {days} days).
          </p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`rounded-2xl border px-3 py-1.5 text-xs font-bold transition ${days === d ? 'border-foreground bg-primary text-primary-foreground' : 'border-border'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          title="Total spend (USD)"
          value={data !== null ? formatCents(data.totals.cost_cents) : '—'}
        />
        <Kpi title="Total calls" value={data !== null ? String(data.totals.total_calls) : '—'} />
        <Kpi title="Cache hits" value={data !== null ? String(data.totals.cached_count) : '—'} />
        <Kpi title="Failures" value={data !== null ? String(data.totals.failure_count) : '—'} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By feature</CardTitle>
          </CardHeader>
          <CardContent>
            {data === null && <div className="h-32 animate-pulse rounded-2xl bg-muted" />}
            {data !== null && data.by_feature.length === 0 && (
              <p className="text-sm text-muted-foreground">No usage yet.</p>
            )}
            {data !== null && data.by_feature.length > 0 && (
              <ul className="space-y-2 text-sm">
                {data.by_feature.map((f) => (
                  <li
                    key={f.feature}
                    className="flex items-center justify-between rounded-2xl border border-border p-3"
                  >
                    <span className="font-medium">{f.feature}</span>
                    <span className="text-xs text-muted-foreground">
                      {f.calls} calls · {Number(f.tokens).toLocaleString()} tok
                    </span>
                    <span className="tabular-nums font-bold">{formatCents(f.cost_cents)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily cost (cents)</CardTitle>
          </CardHeader>
          <CardContent>
            {data === null && <div className="h-32 animate-pulse rounded-2xl bg-muted" />}
            {data !== null && data.by_day.length === 0 && (
              <p className="text-sm text-muted-foreground">No usage yet.</p>
            )}
            {data !== null && data.by_day.length > 0 && (
              <BarSeries
                data={data.by_day.map((d) => ({
                  label: d.day.slice(5),
                  value: Number(d.cost_cents),
                }))}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-black">{value}</p>
      </CardContent>
    </Card>
  );
}

function BarSeries({ data }: { data: Array<{ label: string; value: number }> }) {
  const max = data.reduce((m, d) => Math.max(m, d.value), 1);
  return (
    <div
      className="grid gap-1"
      style={{ gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(0, 1fr))` }}
    >
      {data.map((d) => (
        <div
          key={d.label}
          className="flex flex-col items-center gap-1"
          title={`${d.label}: ${d.value}`}
        >
          <div className="flex h-24 w-full items-end rounded-t bg-muted">
            <div
              className="w-full rounded-t bg-primary"
              style={{ height: `${Math.max(2, Math.round((100 * d.value) / max))}%` }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function formatCents(cents: number | string): string {
  const c = typeof cents === 'string' ? Number(cents) : cents;
  return `$${(c / 100).toFixed(2)}`;
}
